import { Analytics } from '@analytics';
import StarIcon from '@assets/images/Avg_Star.svg';
import LinkIcon from '@assets/images/dashboard/LINK.svg';
import QRIcon from '@assets/images/dashboard/QR.svg';
import RFIDIcon from '@assets/images/dashboard/RFIDGrey.svg';
import AppPromotion from '@components/AppPromotion';
import DashboardBalance from '@components/DashboardBalance';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import TextIconInformationBox from '@components/Onboarding/TextIconInformationBox';
import { TextIconBoxType } from '@components/Onboarding/constants';
import EarningsTrendChart from '@components/TapToTip/EarningsTrendChart';
import { GuideAdviceCard } from '@components/TapToTip/GuideAdvice';
import GroupCard from '@components/TapToTip/Groups/GroupCard';
import MobileConfirmationDialog from '@components/TapToTip/MobileConfirmationDialog';
import AgentSummary from '@components/TapToTip/AgentSummary';
import PaymentLinkQRGeneratorModal from '@components/TapToTip/PaymentLinksQRGeneratorModal';
import { StyleBreakpoints } from '@constants';
import { Avatar, Button, Snackbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert as MuiAlert } from '@material-ui/lab';
import { currencySign, formatRegionalLinks } from '@utils/helpers';
import { useIsFeatureForCompanyEnabled } from '@utils/hooks';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { RemitBalance } from '../../../api/payout';
import RFIDIconColor from '@assets/images/RFID.svg';
import messages from './messages';
import StripeVerificationWarning from '@components/StripeVerificationWarning';
import { getLegacyStripeAccountBalance } from '@api/user';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  }
}));

const navigationButton = {
  tippingQRCode: {
    analyticsLabel: 'view tipping qr code',
    page: 'dashboard',
    link: '/tipdirect/tipdirect'
  },
  payments: {
    analyticsLabel: 'view payments',
    page: 'tip direct',
    link: '/tipdirect/payments'
  }
};

const Dashboard = ({ user }) => {
  const classes = useStyles();
  const history = useHistory();
  const [showAlert, setShouldShowCopyAlert] = useState(false);
  const [description, setDescription] = useState('');
  const [buttons, setButtons] = useState(null);
  const [isConfirmationDialogShown, setIsConfirmationDialogShown] = useState(false);
  const [dialogIcon, setDialogIcon] = useState(null);
  const [shouldShowRemiteAlert, setShouldShowRemitAlert] = useState(false);
  const [isPaymentLnkModalOpen, setIsPaymentLinkModalOpen] = useState(false);
  const isFeatureForCompanyEnabled = useIsFeatureForCompanyEnabled();
  const [apiError, setApiError] = useState(null);
  const [hasLegacyBalance, setHasLegacyBalance] = useState(false);

  React.useEffect(() => {
    if (user?.profile?.stripeConnectCustom?.legacyAccountId) {
      getLegacyStripeAccountBalance()
        .then(res => {
          if (res?.data?.hasBalance) {
            setHasLegacyBalance(true);
          }
        })
        .catch(err => {
          console.error('Failed to check legacy Stripe balance:', err);
        });
    }
  }, [user?.profile?.stripeConnectCustom?.legacyAccountId]);

  const intl = useIntl();

  const linkUrl =
    process.env.REGION == 'EU'
      ? `https://ticketing.tripadmit.com/t/g/${user.userId}`
      : process.env.REGION == 'QA'
        ? `https://qats.tripadmit.com/t/g/${user.userId}`
        : `https://us.tip.direct/t/g/${user.userId}`;

  // Same source of truth as the Get Tips page QR (formatRegionalLinks), so the
  // code shown here matches the one guides download and print.
  const tippingQrUrl = formatRegionalLinks(`t/g/${user.userId}`);
  const tipsDisabled = user.userFeatures?.tipsDisabled ?? user.tipsDisabled;
  const qrSize = typeof window !== 'undefined' && window.innerWidth <= StyleBreakpoints.sm ? 200 : 240;

  const handleCopyClick = () => {
    Analytics.track('copy tipping link', {
      page: 'dashboard'
    });
    navigator.clipboard.writeText(linkUrl);
    setShouldShowCopyAlert(true);
  };

  const handleNavigate = navigationDetails => {
    Analytics.track(navigationDetails.analyticsLabel, {
      page: navigationDetails.page
    });
    history.push(navigationDetails.link);
  };

  //Needs to be connected to payout service
  const handleOnDemandTransfer = async () => {
    try {
      await RemitBalance();

      setShouldShowRemitAlert(true);

      Analytics.track('transfer tips clicked', {
        page: 'dashboard'
      });
    } catch (error) {
      setApiError(error?.message);
    }
  };

  const handleKYCredirect = () => {
    Analytics.track('complete KYC clicked', {
      page: 'dashboard'
    });
    history.push('/KYC');
  };

  const closeConfirmationDialog = () => {
    setIsConfirmationDialogShown(false);
  };

  const hasPaymentLinks = isFeatureForCompanyEnabled('PaymentLinks');
  const hasStripeAccount = Boolean(
    user.profile.stripeConnectCustom?.id || user.profile.stripeConnect?.stripeClientId
  );
  const displayName = user.profile.nickname || `${user.profile.firstName} ${user.profile.lastName}`;
  // sevenDayPercentageDiff arrives preformatted from the backend, e.g. "+16%"
  const sevenDayDiff = user.tippingMetrics?.sevenDayPercentageDiff;
  const showSevenDayDiff =
    typeof sevenDayDiff === 'string' && sevenDayDiff.length > 0 && user.tippingMetrics.allTimeCount > 0;

  return (
    <>
      <TopNav />
      <div className={classes.container}>
        <TipDirectLeftMenu selectedTab={'Dashboard'} />

        <div className="flex-1 px-4 py-6 md:px-8 w-full max-w-7xl mx-auto min-w-0">
          <StripeVerificationWarning user={user} />
          {hasLegacyBalance && (
            <div className="w-full flex justify-center items-center text-center font-bold bg-emerald-50 border border-solid border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl mb-4 cursor-pointer hover:bg-emerald-100 transition-colors" onClick={() => history.push('/tipdirect/balance?historic=true')}>
              Looking for historic payments or your previous balance? <span className="underline ml-1">View Historic Balance</span>
            </div>
          )}
          {user.companyStatus === 'pending' && (
            <div className="w-full flex justify-center items-center text-center font-bold bg-amber-50 border border-solid border-amber-200 text-amber-700 px-4 py-3 rounded-xl mb-4">Account approval pending</div>
          )}
          {/*
            No !hasStripeAccount guard: a stub Stripe account is auto-created on
            the guide's first tip, and a guide can't reach the unclaimed-tips
            limit without receiving tips — so that condition was never true once
            they were paused, and this banner never rendered. isTippingPaused is
            cleared by the account.updated webhook as soon as capabilities go
            active, so a true value means they genuinely can't be paid. (Card 152)
          */}
          {user.userFeatures?.isTippingPaused && (
            <div className="w-full flex justify-center items-center text-center font-bold bg-red-50 border border-solid border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 cursor-pointer hover:bg-red-100 transition-colors" onClick={() => history.push('/payoutlink')}>
              Your tipping link is paused because you've reached the unclaimed tips limit. Click here to set up your payout method and re-enable it.
            </div>
          )}

          <AgentSummary user={user} />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 flex flex-col gap-6 min-w-0">
              {/* Profile card */}
              <div className="w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6 relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none"></div>
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left relative z-10">
                  <div className="relative shrink-0 mb-2 sm:mb-0">
                    <div className="w-24 h-24 rounded-3xl overflow-hidden ring-4 ring-white shadow-lg bg-slate-100 flex items-center justify-center">
                      {user.profile.avatarUrl ? (
                        <img
                          className="w-full h-full object-cover"
                          src={user.profile.avatarUrl}
                          alt="Users profile"
                        />
                      ) : (
                        <Avatar className="!w-full !h-full" />
                      )}
                    </div>
                    {user.averageRatingData.averageRating !== '0' && !hasPaymentLinks && (
                      <div
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-gradient-to-br from-amber-400 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md ring-2 ring-white z-10 whitespace-nowrap"
                        title={`${user.averageRatingData.averageRating} ${intl.formatMessage(
                          messages.averageRating
                        )}`}>
                        <img className="w-3.5" src={StarIcon} alt="" />
                        {user.averageRatingData.averageRating}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 sm:mt-1">
                    <h1 className="text-2xl font-bold text-slate-900 m-0 truncate">{displayName}</h1>
                    {user.companyName && (
                      <div className="text-xs text-slate-400 font-semibold tracking-wide uppercase mt-1">
                        {user.companyName}
                      </div>
                    )}
                    {user.profile.description && (
                      <p className="text-sm text-slate-500 mt-2 mb-0 max-w-md">
                        {user.profile.description}
                      </p>
                    )}

                    <div className="flex items-center justify-center sm:justify-start gap-3 mt-4 flex-wrap">
                      {hasPaymentLinks ? (
                        <button
                          type="button"
                          className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded-xl border-0 cursor-pointer transition-colors"
                          onClick={() => setIsPaymentLinkModalOpen(true)}>
                          <img className="w-5 h-5" src={RFIDIcon} alt="" />
                          {intl.formatMessage(messages.takePayment)}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-indigo-600 hover:text-white text-slate-700 font-semibold text-xs rounded-xl border-0 cursor-pointer transition-all"
                          onClick={handleCopyClick}>
                          <img className="w-4 h-4" src={LinkIcon} alt="" />
                          {intl.formatMessage(messages.copyTipLink)}
                        </button>
                      )}
                      <button
                        type="button"
                        className="p-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl border-0 cursor-pointer transition-colors flex items-center justify-center"
                        title="QR code"
                        onClick={() => handleNavigate(navigationButton.tippingQRCode)}>
                        <img className="w-4 h-4" src={QRIcon} alt="tipping qr code link" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {hasPaymentLinks && (
                <Button
                  className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !rounded-xl !shadow-sm !normal-case !font-bold !text-lg !py-4"
                  variant="contained"
                  startIcon={<img src={RFIDIconColor} alt="" style={{ width: 24, height: 24 }} />}
                  onClick={() => setIsPaymentLinkModalOpen(true)}>
                  {intl.formatMessage(messages.takePayment)}
                </Button>
              )}

              {/*
                Card 119 — sits above the balance because most of what it has to
                say is why the balance is not moving. Renders nothing for admins,
                for payments companies, and for any guide whose account state
                does not trigger a module.
              */}
              <GuideAdviceCard user={user} />

              {hasStripeAccount && (
                <div className="w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6">
                  <div className="text-slate-900 font-bold mb-4">
                    {intl.formatMessage(messages.payoutAndBalance)}
                  </div>
                  <DashboardBalance user={user} />
                </div>
              )}

              {user.tippingMetrics.allTime <= 0 && !hasPaymentLinks && (
                <div className="w-full bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6 flex flex-col items-center">
                  <TextIconInformationBox card={TextIconBoxType.NoTipsYet} whiteBackground />
                </div>
              )}

              {!hasPaymentLinks && <EarningsTrendChart user={user} />}
            </div>

            <div className="lg:col-span-5 flex flex-col gap-6 min-w-0">
              {!hasPaymentLinks && (
                <div
                  className="w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6 flex flex-col items-center text-center"
                  data-testid="dashboard-qr-card">
                  <div className="text-slate-900 font-bold">
                    {intl.formatMessage(tipsDisabled ? messages.scanToReview : messages.scanToTip)}
                  </div>
                  <div className="text-sm text-slate-500 mt-1 mb-5 max-w-xs">
                    {intl.formatMessage(messages.showQrToGuest)}
                  </div>
                  <div className="flex justify-center items-center p-2.5 border-[3px] border-solid border-slate-900 rounded-md bg-white">
                    <QRCodeCanvas value={tippingQrUrl} size={qrSize} />
                  </div>
                </div>
              )}

              {!hasPaymentLinks && (
                <div className="w-full">
                  <div className="w-full bg-gradient-to-br from-indigo-500 to-purple-600 grain rounded-3xl p-6 text-white shadow-xl shadow-indigo-200 relative overflow-hidden group">
                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
                    <div className="text-white font-bold text-sm tracking-wider uppercase mb-4 relative z-10">
                      {intl.formatMessage(messages.activityOverview)}
                    </div>
                    <div className="flex flex-col gap-3 relative z-10">
                      <div className="flex flex-row justify-between items-center bg-white/20 px-4 py-3 rounded-xl backdrop-blur-md">
                        <div className="text-white text-sm font-semibold">
                          {intl.formatMessage(messages.lastSevenDays)}
                          {showSevenDayDiff && (
                            <span
                              className="ml-2 text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded"
                              title={intl.formatMessage(messages.vsPreviousWeek)}>
                              {sevenDayDiff.startsWith('-') ? '▼' : '▲'}{' '}
                              {sevenDayDiff.replace(/^[+-]/, '')}
                            </span>
                          )}
                        </div>
                        <div className="text-2xl font-bold">
                          {`${currencySign(user.profile.currency)}${(
                            user.tippingMetrics.lastSevenDays / 100
                          ).toFixed(2)}`}
                        </div>
                      </div>
                      <div className="flex flex-row justify-between items-center bg-white/20 px-4 py-3 rounded-xl backdrop-blur-md">
                        <div className="text-white text-sm font-semibold">
                          {intl.formatMessage(messages.totalTips)}
                        </div>
                        <div className="text-2xl font-bold">{user.tippingMetrics.allTimeCount}</div>
                      </div>
                      <div className="flex flex-row justify-between items-center bg-white/20 px-4 py-3 rounded-xl backdrop-blur-md">
                        <div className="text-white text-sm font-semibold">
                          {intl.formatMessage(messages.totalAllTime)}
                        </div>
                        <div className="text-2xl font-bold">{`${currencySign(
                          user.profile.currency
                        )}${(user.tippingMetrics.allTime / 100).toFixed(2)}`}</div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full">
                    <Button
                      className="!w-full !mt-4 !bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !px-5 !py-2.5 !rounded-xl !shadow-sm !normal-case"
                      variant="contained"
                      onClick={() => handleNavigate(navigationButton.payments)}>
                      {intl.formatMessage(messages.viewPayments)}
                    </Button>
                  </div>
                </div>
              )}

              {Boolean(Object.keys(user.groupDetails).length) && (
                <div className="w-full bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6">
                  <GroupCard
                    group={user.groupDetails}
                    user={user}
                    isUserInGroup={true}
                    fullSize={true}
                    setDescription={setDescription}
                    setButtons={setButtons}
                    setIsConfirmationDialogShown={setIsConfirmationDialogShown}
                    setDialogIcon={setDialogIcon}
                    closeConfirmationDialog={closeConfirmationDialog}
                  />
                </div>
              )}

              <div className="w-full bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6">
                <div className="w-full bg-white rounded-2xl shadow-sm border border-solid border-slate-200 px-10 py-6 flex flex-row justify-center items-center">
                  <AppPromotion source={'dashboard'} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaymentLinkQRGeneratorModal
        isOpen={isPaymentLnkModalOpen}
        setIsOpen={setIsPaymentLinkModalOpen}
        currency={user.profile.currency}
        userId={user.userId}
        companyName={user.companyName}
      />

      <Snackbar
        open={showAlert}
        autoHideDuration={10000}
        onClose={() => {
          setShouldShowCopyAlert(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <MuiAlert
          variant="filled"
          elevation={6}
          severity="success"
          onClose={() => {
            setShouldShowCopyAlert(false);
          }}>
          {intl.formatMessage(messages.copiedToClipboard)}
        </MuiAlert>
      </Snackbar>

      <Snackbar
        open={shouldShowRemiteAlert}
        onClose={() => {
          setShouldShowRemitAlert(false);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <MuiAlert
          variant="filled"
          elevation={6}
          severity="info"
          onClose={() => {
            setShouldShowRemitAlert(false);
          }}>
          {intl.formatMessage(messages.remitSuccessful)}
        </MuiAlert>
      </Snackbar>

      <Snackbar
        open={Boolean(apiError)}
        onClose={() => {
          setApiError(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <MuiAlert
          variant="filled"
          elevation={6}
          severity="error"
          onClose={() => {
            setApiError(null);
          }}>
          {apiError}
        </MuiAlert>
      </Snackbar>

      <MobileConfirmationDialog
        isShown={isConfirmationDialogShown}
        buttons={buttons}
        description={description}
        close={closeConfirmationDialog}
        icon={dialogIcon}
      />
    </>
  );
};

const mapStateToProps = state => ({
  user: state.user?.user
});

export default connect(mapStateToProps)(Dashboard);
